/**
 * The shared shell: once the page hero has scrolled past, the sticky header
 * collapses and shows a copy of the page title. The header also holds the
 * theme toggle, remembered in localStorage: a click always flips what is shown,
 * and picking the theme the system uses anyway means following the system again.
 *
 * Built to server/static/layout.min.js with `edwh build-js` (bun). It is loaded
 * as a module, so nothing here leaks into the global scope.
 */

/** Collapse the sticky header once `collapse_at` has scrolled past. */
function collapse_on_scroll($header: HTMLElement, collapse_at: number): void {
    function update_header(): void {
        $header.classList.toggle("is-compact", window.scrollY >= collapse_at);
    }

    update_header();
    window.addEventListener("scroll", update_header, { passive: true });
}

type Theme = "auto" | "dark" | "light";

const THEME_LABELS: Record<Theme, string> = {
    auto: "Theme: follow the system",
    dark: "Theme: dark",
    light: "Theme: light",
};
const $system_dark = window.matchMedia("(prefers-color-scheme: dark)");

function current_theme(): Theme {
    const theme = document.documentElement.dataset.theme;
    return theme === "dark" || theme === "light" ? theme : "auto";
}

function shown_theme(): "dark" | "light" {
    const theme = current_theme();
    if (theme !== "auto") {
        return theme;
    }
    return $system_dark.matches ? "dark" : "light";
}

/** Apply and remember a theme; "auto" leaves it to prefers-color-scheme. */
function set_theme(theme: Theme, $toggle: HTMLButtonElement): void {
    if (theme === "auto") {
        delete document.documentElement.dataset.theme;
    } else {
        document.documentElement.dataset.theme = theme;
    }
    try {
        if (theme === "auto") {
            localStorage.removeItem("theme");
        } else {
            localStorage.setItem("theme", theme);
        }
    } catch {
        // private mode: it still applies until the page is left
    }
    label_toggle($toggle);
}

function label_toggle($toggle: HTMLButtonElement): void {
    const label = `${THEME_LABELS[current_theme()]} (click for ${shown_theme() === "dark" ? "light" : "dark"})`;
    $toggle.setAttribute("aria-label", label);
    $toggle.title = label;
}

function flip_theme($toggle: HTMLButtonElement): void {
    const next = shown_theme() === "dark" ? "light" : "dark";
    const system = $system_dark.matches ? "dark" : "light";
    set_theme(next === system ? "auto" : next, $toggle);
}

function init_theme_toggle(): void {
    const $toggle = document.querySelector<HTMLButtonElement>(".theme-toggle");
    if (!$toggle) {
        return;
    }
    set_theme(current_theme(), $toggle);
    $toggle.addEventListener("click", () => flip_theme($toggle));
    // following the system: the label names the other theme, which may have changed
    $system_dark.addEventListener("change", () => label_toggle($toggle));
}

function init(): void {
    init_theme_toggle();

    const $header = document.querySelector<HTMLElement>(".site-header");
    const $page_title = document.querySelector<HTMLElement>(".page-title");
    const $section_title = document.querySelector<HTMLElement>(".site-section-title");

    // a page without a hero, or without the shell, has nothing to collapse
    if (!$header || !$page_title || !$section_title) {
        return;
    }

    const $compact_title = $page_title.cloneNode(true) as HTMLElement;
    $compact_title.removeAttribute("id");
    $section_title.append($compact_title);

    const $page_section = $page_title.closest<HTMLElement>(".page-hero");
    const collapse_at = $page_section
        ? $page_section.offsetTop + $page_section.offsetHeight
        : $header.offsetTop + $header.offsetHeight;

    collapse_on_scroll($header, collapse_at);
}

init();

export {};
